import React, { useMemo } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import RecordNotFound from "../../../../restaurant/shared/recordNotFound.js";
import CheckoutSidebar from "./cashier-home-layout/components/CheckoutSidebar.tsx";
import { useCashierHomeLayout } from "./cashier-home-layout/hooks/useCashierHomeLayout.ts";
import { useSelector } from "react-redux";
import { isGuestGateAsking, selectGuestCountFor } from "../../../../../redux/slices/guestCount";
import "./cashier-home-layout/split-payment-ui.css";
import {useCustomerDisplay} from "../../../shared/customer-display/useCustomerDisplay";

const CashierHomeLayout = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const orderType = useMemo(
    () => (location.state?.type ? location.state?.type : searchParams.get("type")) ?? "",
    [location.state?.type, searchParams],
  );
  const state = useCashierHomeLayout({ tableId, orderType });

  const displayEvent = useMemo(() => {
    const items = (state.order.orderItems ?? []).map((item) => ({
      name: String(item.item_name ?? item.item_name_en ?? item.name ?? "ITEM"),
      quantity: Number(item.quantity) || 0,
    }));
    const total = Number(state.order.orderPriceDetail?.amount_after_discount
      ?? state.order.orderPriceDetail?.great_grand_total
      ?? 0) || 0;
    const activeOrder = state.order.tableDetail?.activeOrder;
    if (state.ui.activeSidebarTab === "payment") {
      const originalTotal = Number(activeOrder?.great_grand_total ?? total) || total;
      const remaining = Number(activeOrder?.remaining_amount);
      const safeRemaining = Number.isFinite(remaining) && remaining >= 0 ? remaining : originalTotal;
      return {type: "payment" as const, total: originalTotal, paid: Math.max(0, originalTotal - safeRemaining), remaining: safeRemaining};
    }
    return {type: "order" as const, items, total};
  }, [state]);
  useCustomerDisplay(displayEvent);

  const answeredGuestCount = useSelector(selectGuestCountFor(tableId));
  const guestGateAsking =
    location.pathname?.includes("/categories/") &&
    isGuestGateAsking({
      capacity: state.order?.tableDetail?.capacity,
      activeOrder: state.order?.tableDetail?.activeOrder,
      answered: answeredGuestCount,
    });

  // Typed as Record<string, string> so the spread bypasses React's boolean
  // `inert` prop typing, matching the QR gate's own workaround.
  const inertWhileAsking: Record<string, string> = guestGateAsking ? { inert: "" } : {};

  return (
    <div className="dashboard-cashier">
      <Outlet />
      <div
        className={`dashboard-cashier-right${guestGateAsking ? " qr-guest-gate-blurred" : ""}`}
        aria-hidden={guestGateAsking || undefined}
        {...inertWhileAsking}
      >
        {!tableId ? (
          <div className="table-not-found">
            <RecordNotFound heading="no_table_selected" />
          </div>
        ) : (
          <CheckoutSidebar state={state} />
        )}
      </div>
    </div>
  );
};

export default CashierHomeLayout;
